import Quill from 'quill';
const Clipboard = Quill.import('modules/clipboard');
const Delta = Quill.import('delta');

/* eslint-disable */
/** @class Abstract class representing a tool for a Quill Editor toolbar. */
class QuillToolbarItem {
  options: any;
  qlFormatsEl: HTMLSpanElement | undefined;
  quill: any;
  toolbar: any;
  toolbarEl: any;
  constructor(options: any) {
    const me = this;
    me.options = options;

    me.qlFormatsEl = document.createElement('span');
    me.qlFormatsEl.className = 'ql-formats';
  }
  /**
   * Attaches this tool to the given Quill Editor instance.
   *
   * @param {Quill} quill - The Quill Editor instance that this tool should get added to.
   */
  attach(quill: any) {
    const editor = quill.getEditor();
    if (editor) {
      this.quill = quill;
      this.toolbar = editor.getModule('toolbar');
      this.toolbarEl = this.toolbar.container;
      this.toolbarEl.appendChild(this.qlFormatsEl);
    }
  }
  /**
   * Detaches this tool from the given Quill Editor instance.
   *
   * @param {Quill} quill - The Quill Editor instance that this tool should get added to.
   */
  detach(_quill: any) {
    this.toolbarEl.removeChild(this.qlFormatsEl);
  }
  /**
   * Calculate the width of text.
   *
   * @param {string} text - The text of which the length should be calculated.
   * @param {string} [font="500 14px 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif"] - The font CSS that should be applied to the text before calculating the width.
   */
  _getTextWidth(
    text: any,
    font = "500 14px 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
  ) {
    const canvas =
      (this._getTextWidth as any).canvas ||
      ((this._getTextWidth as any).canvas = document.createElement('canvas'));

    const context = canvas.getContext('2d');

    // Add null check for context
    if (!context) {
      // Fallback: estimate width based on character count
      // This is a rough estimation - adjust multiplier as needed
      return text.length * 8;
    }

    context.font = font;
    const metrics = context.measureText(text);

    return metrics.width;
  }
  /**
   * Add a global CSS rule to the document.
   *
   * @param {string} cssRule - CSS rules
   */
  _addCssRule(cssRule: string) {
    const style = document.createElement('style');
    document.head.appendChild(style);
    style.sheet!.insertRule(cssRule, 0);
  }
  /**
   * Generate a random ID.
   *
   * @returns {string} random 10-digit ID
   */
  _generateId() {
    return Math.random().toString().substr(2, 10);
  }
}

/** @class Class representing a dropdown tool for a Quill Editor toolbar. */
export class QuillToolbarDropDown extends QuillToolbarItem {
  id: any;
  dropDownEl: HTMLSpanElement | undefined;
  dropDownPickerEl: any;
  dropDownPickerLabelEl: any;
  /**
   * Creates an instance of QuillToolbarDropDown.
   *
   * @constructor
   * @param {object} [options] - The options/settings for this QuillToolbarDropDown.
   * @param {string} [options.id=`dropdown-${random10digitNumber}`] - The ID of the quill tool.
   * @param {string} [options.label=""] - The default label that is being displayed before making a selection.
   * @param {boolean} [options.rememberSelection=true] - Automatically change the label to the current selection.
   * @param {object} [options.items={}] - The default items this dropdown will have. Needs to be a key-value-object (key=visible label; value=actual value).
   */
  constructor(options: { label: string; rememberSelection: boolean }) {
    super(options);
    const me = this;

    me.id = me.options.id || `dropdown-${me._generateId()}`;

    const qlPicker = document.createElement('span');
    qlPicker.className = `ql-${me.id} ql-picker`;
    me.qlFormatsEl!.appendChild(qlPicker);

    const qlPickerLabel = document.createElement('span');
    qlPickerLabel.className = 'ql-picker-label';
    qlPicker.appendChild(qlPickerLabel);
    qlPickerLabel.addEventListener('click', function (_e) {
      qlPicker.classList.toggle('ql-expanded');
    });
    window.addEventListener('click', function (e) {
      if (!qlPicker.contains((e as any).target)) {
        qlPicker.classList.remove('ql-expanded');
      }
    });

    const qlPickerOptions = document.createElement('span');
    qlPickerOptions.className = 'ql-picker-options';
    qlPicker.appendChild(qlPickerOptions);

    me.dropDownEl = qlPicker;
    me.dropDownPickerEl = me.dropDownEl.querySelector('.ql-picker-options');
    me.dropDownPickerLabelEl = me.dropDownEl.querySelector('.ql-picker-label');
    me.dropDownPickerLabelEl.innerHTML = `<svg viewBox="0 0 18 18"> <polygon class="ql-stroke" points="7 11 9 13 11 11 7 11"></polygon> <polygon class="ql-stroke" points="7 7 9 5 11 7 7 7"></polygon> </svg>`;

    me.setLabel(me.options.label || '');
    me.setItems(me.options.items || {});

    me._addCssRule(`
      .ql-snow .ql-picker.ql-${me.id} .ql-picker-label::before, .ql-${me.id} .ql-picker.ql-size .ql-picker-item::before {
        content: attr(data-label);
      }
    `);
  }
  /**
   * Set the items for this dropdown tool.
   *
   * @param {object} items - Needs to be a key-value-object (key=visible label; value=actual value).
   */
  setItems(items: { [s: string]: unknown }) {
    const me = this;
    for (const [label, value] of Object.entries(items)) {
      const newItemEl = document.createElement('span');
      newItemEl.className = 'ql-picker-item';
      newItemEl.innerHTML = label;
      newItemEl.setAttribute('data-value', value as any);
      newItemEl.onclick = function () {
        me.dropDownEl!.classList.remove('ql-expanded');
        if (me.options.rememberSelection) {
          [
            'Size',
            'Letter spacing',
            'Line height',
            'Font',
            'Special chars',
          ].includes(me.options.label)
            ? me.setLabel(`${me.options.label}: ${label}`)
            : me.setLabel(label);
        }
        if (me.onSelect) me.onSelect(label, value, me.quill);
      };
      me.dropDownPickerEl.appendChild(newItemEl);
    }
  }

  /**
   * Set the label for this dropdown tool and automatically adjust the width to fit the label.
   *
   * @param {String} newLabel - The new label that should be set.
   */
  setLabel(newLabel: string) {
    const me = this;
    const requiredWidth = `${me._getTextWidth(newLabel) + 30}px`;
    me.dropDownPickerLabelEl.style.width = requiredWidth;
    me.dropDownPickerLabelEl.setAttribute('data-label', newLabel);
  }
  /**
   * A callback that gets called automatically when the dropdown selection changes. This callback is expected to be overwritten.
   *
   * @param {string} label - The label of the newly selected item.
   * @param {string} value - The value of the newly selected item.
   * @param {Quill} quill - The Quill instance the dropdown tool is attached to.
   */
  onSelect(_label: string, _value: unknown, _quill: any) {}
}

// comment out this class as it's not used
/** @class Class representing a button tool for a Quill Editor toolbar. */
// class QuillToolbarButton extends QuillToolbarItem {
//   id: any;
//   qlButton: HTMLButtonElement | undefined;
//   /**
//    * Creates an instance of QuillToolbarButton.
//    *
//    * @constructor
//    * @param {object} [options] - The options/settings for this QuillToolbarButton.
//    * @param {string} [options.id=`button-${random10digitNumber}`] - The ID of the quill tool.
//    * @param {string} [options.value] - The default hidden value of the button.
//    * @param {string} options.icon - The default icon this button tool will have.
//    */
//   constructor(options: any) {
//     super(options);
//     const me = this;

//     me.id = me.options.id || `button-${me._generateId()}`;

//     me.qlButton = document.createElement('button');
//     me.qlButton.className = `ql-${me.id}`;
//     me.setValue(me.options.value);
//     me.setIcon(me.options.icon);
//     me.qlButton.onclick = function () {
//       (me as any).onClick(me.quill);
//     };
//     me.qlFormatsEl?.appendChild(me.qlButton);
//   }
//   /**
//    * Set the icon for this button tool.
//    *
//    * @param {string} imageHtml - The <svg> or <img> HTML tag to use as an icon. (Make sure it's 18x18 in size.)
//    */
//   setIcon(imageHtml: string) {
//     const me = this;
//     me.qlButton!.innerHTML = imageHtml;
//   }
//   /**
//    * Set the hidden value of this button tool.
//    *
//    * @param {string} value - The hidden value of the button.
//    */
//   setValue(value: string) {
//     const me = this;
//     me.qlButton!.value = value;
//   }
//   /**
//    * Get the hidden value of this button tool.
//    *
//    * @returns {string} The hidden value of the button.
//    */

//   getValue() {
//     const me = this;
//     return me.qlButton!.value;
//   }
//   /**
//    * A callback that gets called automatically when the button is clicked, tapped, or triggered with the keyboard, etc. This callback is expected to be overwritten.
//    *
//    * @param {Quill} quill - The Quill instance the button tool is attached to.
//    */
//   onClick(_quill: any) {}
// }

export class PlainClipboard extends Clipboard {
  onPaste(e: {
    preventDefault: () => void;
    clipboardData: { getData: (arg0: string) => any };
  }) {
    e.preventDefault();
    const range = this.quill.getSelection();
    const text = e.clipboardData.getData('text/plain');
    const delta = new Delta()
      .retain(range.index)
      .delete(range.length)
      .insert(text);
    const index = text.length + range.index;
    const length = 0;
    this.quill.updateContents(delta, 'silent');
    this.quill.setSelection(index, length, 'silent');
    this.quill.scrollIntoView();
  }
}
